import React from "react";
import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import { ApolloProvider } from "@apollo/client";
import { RouterProvider } from "react-router-dom";
import router from "./routes";
import ErrorBoundary from "./components/ErrorBoundary";
import { createApolloClient } from "./lib/apollo";
import { ThemeProvider } from "./components/ui/theme-provider";
import { Toaster } from "./components/ui/sonner";
// Import clerk mock to initialize it in E2E mode
import "./lib/clerk-mock";
import { mockUseAuth } from "./lib/clerk-mock";

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const isE2EMode = import.meta.env.VITE_E2E_MODE === 'true';

// Use mocked auth hook in E2E mode
const useAuthHook = isE2EMode ? mockUseAuth : useAuth;

// Mock Clerk Provider for E2E testing
function MockClerkProvider({ children }: { children: React.ReactNode }) {
  return <div data-testid="mock-clerk-provider">{children}</div>;
}

// Component that provides Apollo Client with Clerk authentication
function AppWithApollo() {
  const { getToken } = useAuthHook();

  // Create Apollo Client with Clerk token getter - memoize to prevent recreation
  const apolloClient = React.useMemo(
    () => createApolloClient(() => {
      // Validate Clerk template configuration
      const templateName = "a2a_backend";
      console.log('[Clerk Auth] Requesting token with template:', templateName);
      
      return getToken({ template: templateName }).then(token => {
        if (!token) {
          console.error('[Clerk Auth] ❌ Failed to get token - possible template configuration issue', {
            template: templateName,
            timestamp: new Date().toISOString(),
            suggestion: 'Check if "a2a_backend" template exists in Clerk dashboard'
          });
        } else {
          console.log('[Clerk Auth] ✅ Token retrieved successfully with template', {
            template: templateName,
            tokenLength: token.length,
            tokenPrefix: `${token.substring(0, 10)}...`,
            timestamp: new Date().toISOString()
          });
        }
        return token;
      }).catch(error => {
        console.error('[Clerk Auth] ❌ Token retrieval failed', {
          template: templateName,
          error: error.message,
          timestamp: new Date().toISOString(),
          suggestions: [
            'Check Clerk dashboard for "a2a_backend" JWT template',
            'Verify template name spelling and case sensitivity',
            'Ensure template is active and properly configured'
          ]
        });
        throw error;
      });
    }),
    [getToken],
  );

  return (
    <ApolloProvider client={apolloClient}>
      <ThemeProvider defaultTheme="system" storageKey="theme">
        <RouterProvider router={router} />
        <Toaster />
      </ThemeProvider>
    </ApolloProvider>
  );
}

function App() {
  // Skip key validation in E2E mode since we're using mocked Clerk
  if (!isE2EMode && !clerkPubKey) {
    return (
      <div>
        Error: Clerk publishable key is not configured. Please set VITE_CLERK_PUBLISHABLE_KEY.
      </div>
    );
  }

  return (
    <ErrorBoundary>
      {isE2EMode ? (
        <MockClerkProvider>
          <AppWithApollo />
        </MockClerkProvider>
      ) : (
        <ClerkProvider publishableKey={clerkPubKey!}>
          <AppWithApollo />
        </ClerkProvider>
      )}
    </ErrorBoundary>
  );
}

export default App;
